import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMike } from '../../context/MikeContext';
import { useChatbot } from '../../hooks/useChatbot';
import ChatHeader from './chat/ChatHeader';
import ChatMessages from './chat/ChatMessages';
import ChatInput from './chat/ChatInput';

/**
 * ChatWidget - Global floating chat widget for Mike AI Assistant
 *
 * Visibility Rules:
 * - Meijer (company_id 1121): Globally available across all pages
 * - Non-Meijer: Only visible on Company Admin Dashboard
 *
 * Renders as a floating button in bottom-right corner when minimized.
 * Expands to a 420×600px chat window when open.
 * Conversation persists across page navigation and view changes.
 */
const ChatWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { setCurrentPage, isExpanded } = useMike();
  const {
    messages,
    isLoading,
    isOpen,
    error,
    sendMessage,
    toggleChat,
    clearChat
  } = useChatbot();

  // Horizontal drag state
  const [offsetX, setOffsetX] = useState(0);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartOffsetX = useRef(0);
  const chatRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    // Don't start drag on button clicks
    if (e.target.closest('button')) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartOffsetX.current = offsetX;
    document.body.style.userSelect = 'none';

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const newOffset = dragStartOffsetX.current + delta;

      // Clamp so widget stays on screen
      if (chatRef.current) {
        const rect = chatRef.current.getBoundingClientRect();
        const maxLeft = -(window.innerWidth - rect.width - 60);
        const maxRight = rect.right - rect.width + 30;
        setOffsetX(Math.max(maxLeft, Math.min(maxRight, newOffset)));
      } else {
        setOffsetX(newOffset);
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [offsetX]);

  // Reset position when chat is closed
  useEffect(() => {
    if (!isOpen) setOffsetX(0);
  }, [isOpen]);

  // Sync current page to MikeContext for Meijer hybrid mode
  React.useEffect(() => {
    setCurrentPage(location.pathname);
  }, [location.pathname, setCurrentPage]);

  // Lifecycle and prop/state logging (always run hooks in same order)
  React.useEffect(() => {
    console.log('[Mike][ChatWidget]', 'mounted (global)');
    return () => console.log('[Mike][ChatWidget]', 'unmounted');
  }, []);

  React.useEffect(() => {
    console.log('[Mike][ChatWidget]', 'state change', { isOpen, isLoading, messages: (messages || []).length, hasError: !!error });
  }, [isOpen, isLoading, messages?.length, !!error]);

  // Visibility check (AFTER hooks to maintain hook order)
  const isMeijer = user?.company?.id === "1121";
  const isCompanyAdminDashboard = location.pathname === '/company_admin_dashboard' || location.pathname === '/company_admin_dashboard/';

  // Show if: Meijer (any page) OR non-Meijer on Company Admin Dashboard
  if (!isMeijer && !isCompanyAdminDashboard) {
    return null;
  }

  return (
    <>
      {/* Floating Button (when closed) - Enhanced Design */}
      {!isOpen && (
        <>
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  box-shadow: 0 6px 20px rgba(0, 86, 179, 0.4), 0 0 0 0 rgba(0, 86, 179, 0.4);
                }
                50% {
                  box-shadow: 0 6px 20px rgba(0, 86, 179, 0.4), 0 0 0 10px rgba(0, 86, 179, 0);
                }
              }

              @keyframes float {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-5px); }
              }
            `}
          </style>

          <div
            onClick={toggleChat}
            style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px 14px 18px',
              background: 'linear-gradient(135deg, #0056b3 0%, #003d82 100%)',
              color: 'white',
              borderRadius: '50px',
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(0, 86, 179, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 1000,
              animation: 'pulse 2s infinite, float 3s ease-in-out infinite'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
              e.currentTarget.style.boxShadow = '0 12px 28px rgba(0, 86, 179, 0.5)';
              e.currentTarget.style.animation = 'none'; // Stop animations on hover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 86, 179, 0.4)';
              e.currentTarget.style.animation = 'pulse 2s infinite, float 3s ease-in-out infinite';
            }}
          >
            {/* Icon Circle */}
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden'
            }}>
              <img
                src="/icons/mike.png"
                alt="Mike Assistant"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
              />
            </div>

            {/* Text Content */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: '1.2'
            }}>
              <span style={{
                fontSize: '18px',
                fontWeight: '700',
                letterSpacing: '0.5px'
              }}>
                Ask Mike
              </span>
              <span style={{
                fontSize: '12px',
                opacity: 0.95,
                fontWeight: '400',
                letterSpacing: '0.3px'
              }}>
                DST Assistant
              </span>
            </div>
          </div>
        </>
      )}

      {/* Chat Window (when open) */}
      {isOpen && (
        <div
          ref={chatRef}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: isExpanded ? '1800px' : '420px',
            height: isExpanded ? '750px' : '650px',
            maxWidth: 'calc(100vw - 60px)',
            transform: `translateX(${offsetX}px)`,
            transition: isDragging.current
              ? 'width 0.3s ease, height 0.3s ease'
              : 'width 0.3s ease, height 0.3s ease, transform 0.15s ease',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0, 86, 179, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'slideUp 0.3s ease-out',
            border: '1px solid rgba(0, 86, 179, 0.1)'
          }}
        >
          <style>
            {`
              @keyframes slideUp {
                from {
                  opacity: 0;
                  transform: translateY(20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>

          {/* Header (drag handle) */}
          <ChatHeader onMinimize={toggleChat} onClear={clearChat} onMouseDown={handleMouseDown} isDragging={isDragging.current} />

          {/* Messages */}
          <ChatMessages messages={messages} isLoading={isLoading} error={error} />

          {/* Input */}
          <ChatInput onSend={sendMessage} disabled={isLoading} />
        </div>
      )}
    </>
  );
};

export default ChatWidget;
